import './Typography.css';
import React from 'react';
import styled, { css } from 'styled-components';
import { TextTransform } from '../components/types';
import { themeColors as colors } from '../theme/semanticTokens/colors';
import { themeFonts as fonts } from '../theme/semanticTokens/fonts';

type TypographyVariant =
  | 'heading1Bold'
  | 'heading2'
  | 'heading2Bold'
  | 'heading2Semi'
  | 'heading3'
  | 'heading3Bold'
  | 'heading4'
  | 'heading4Bold'
  | 'body1'
  | 'body1Semi'
  | 'body1Bold'
  | 'body2'
  | 'body2Bold'
  | 'body3'
  | 'body3Bold';

export interface TypographyProps {
  variant?: TypographyVariant;
  color?: string;
  children: React.ReactNode;
  textTransform?: TextTransform;
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
}

interface StyledTypographyProps {
  $textTransform?: TextTransform;
  $variant: TypographyVariant;
  $color: string;
  $clickable: boolean;
}

const typographyStyles = {
  heading1Bold: css`
    font-family: ${fonts.families.polySans};
    font-size: ${fonts.sizes.xxl};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.xl};
  `,
  heading2: css`
    font-family: ${fonts.families.polySans};
    font-size: ${fonts.sizes.xl};
    font-style: normal;
    font-weight: ${fonts.weights.regular};
    line-height: ${fonts.lineHeights.lg};
  `,

  heading2Semi: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.lg};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.lg};
  `,

  heading2Bold: css`
    font-family: ${fonts.families.polySans};
    font-size: ${fonts.sizes.xl};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.lg};
  `,

  heading3: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.lg};
    font-style: normal;
    font-weight: ${fonts.weights.regular};
    line-height: ${fonts.lineHeights.lg};
  `,

  heading3Bold: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.lg};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.lg};
  `,

  heading4Bold: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.base};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.md};
  `,

  heading4: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.base};
    font-style: normal;
    font-weight: ${fonts.weights.regular};
    line-height: ${fonts.lineHeights.md};
  `,

  body1: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.sm};
    font-style: normal;
    font-weight: ${fonts.weights.regular};
    line-height: ${fonts.lineHeights.sm};
  `,

  body1Semi: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.sm};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.md};
  `,

  body1Bold: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.sm};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.sm};
  `,

  body2: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.xs};
    font-style: normal;
    font-weight: ${fonts.weights.regular};
    line-height: ${fonts.lineHeights.sm};
  `,

  body2Bold: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.xs};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.xs};
  `,

  body3: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.xs};
    font-style: normal;
    font-weight: ${fonts.weights.regular};
    line-height: ${fonts.lineHeights.xs};
    letter-spacing: 0.24px;
    text-transform: uppercase;
  `,

  body3Bold: css`
    font-family: ${fonts.families.roboto};
    font-size: ${fonts.sizes.xs};
    font-style: normal;
    font-weight: ${fonts.weights.semiBold};
    line-height: ${fonts.lineHeights.xs};
    letter-spacing: 0.24px;
    text-transform: uppercase;
  `
};

const StyledTypography = styled.div<StyledTypographyProps>`
  color: ${(props) => props.$color || colors.text.primary};
  ${(props) => typographyStyles[props.$variant]}
  text-transform: ${(props) => props.$textTransform || 'none'};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'inherit')};
`;

export const Typography: React.FC<TypographyProps> = ({
  variant = 'body1',
  color = colors.text.primary,
  textTransform,
  onClick,
  children,
  className,
  'data-testid': dataTestId
}) => {
  const handleClick = onClick
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        onClick();
      }
    : undefined;

  return (
    <StyledTypography
      as={onClick ? 'a' : 'div'}
      href={onClick ? '#' : undefined}
      className={className}
      $variant={variant}
      $color={color}
      $textTransform={textTransform}
      $clickable={!!onClick}
      onClick={handleClick}
      data-testid={dataTestId}
    >
      {children}
    </StyledTypography>
  );
};
